import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Path, Question, Signpost } from "@phosphor-icons/react";
import { FeatureTour } from "@/components/ui/feature-tour";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthStatus } from "@/hooks/useAuthStatus";
import { requestFirstRunTour } from "./FirstRunTour";
import {
  stopsForLocation,
  useStepNavigation,
  useTourStops,
  type FirstRunStep,
} from "./tourStops";

/**
 * Help on demand: a question mark in the top bar that opens a small menu with two
 * walks. "Tour this page" pulls up the stop for whatever page the person is on — the
 * same stop the first-run tour makes there, numbered parts and all — without walking
 * the rest. On Live it starts with the work queue and the top bar, since they live
 * there; on one customer or one service template it walks that page's own stops, in
 * place (RUT-4340). It is left out on a page the tour has nothing to say about.
 * "Take the full tour" replays the first-run lap from Live, the same as "Show me
 * around" in the sidebar.
 */
export function PageHelpButton() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { orgId } = useAuthStatus();
  // The stops being walked, fixed at the moment the walk starts. A stop that opens a
  // Settings tab changes the location as it comes up; the walk must not re-resolve
  // itself to that one tab and lose the rest.
  const [walk, setWalk] = useState<FirstRunStep[] | null>(null);
  // Opening the menu re-reads the sidebar, so "Tour this page" is there on a fresh load.
  const [menuOpen, setMenuOpen] = useState(false);
  const { header, pages, details } = useTourStops({
    enabled: walk !== null || menuOpen,
  });
  const here = useMemo(
    () => stopsForLocation(pages, pathname, search, details),
    [pages, details, pathname, search],
  );
  const stops = useMemo(
    () => (pathname === "/assistant" ? [...header, ...here] : here),
    [header, here, pathname],
  );
  const handleStepId = useStepNavigation(walk ?? stops);

  if (stops.length === 0 && !orgId && walk === null) return null;

  // The page's own name, not the header stops Live borrows.
  const pageName = here[0]?.short;

  const startFullTour = () => {
    if (!orgId) return;
    setWalk(null);
    requestFirstRunTour(orgId);
    navigate("/assistant");
  };

  const options = [
    ...(stops.length > 0
      ? [
          {
            key: "page",
            label: "Tour this page",
            subtitle: pageName
              ? `${pageName} · ${stops.length} ${stops.length === 1 ? "stop" : "stops"}`
              : `${stops.length} ${stops.length === 1 ? "stop" : "stops"}`,
            icon: <Signpost className="h-4 w-4" weight="light" />,
            color: "bg-primary/10 text-primary",
            onSelect: () => setWalk(stops),
            disabled: false,
          },
        ]
      : []),
    {
      key: "full",
      label: "Take the full tour",
      subtitle: "Every page, a few minutes. Starts on Live.",
      icon: <Path className="h-4 w-4" weight="light" />,
      color: "bg-orange-50 text-orange-600",
      onSelect: startFullTour,
      disabled: !orgId,
    },
  ];

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {/* Bare Tooltip: the header sits under the sidebar's ambient TooltipProvider. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Help and tours"
                data-tour="header-help"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-white/85 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <Question className="h-[18px] w-[18px]" />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Help and tours</TooltipContent>
        </Tooltip>

        <DropdownMenuContent side="bottom" align="end" className="w-64 p-1.5">
          <div className="px-2 py-1.5 mb-1">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Show me around
            </p>
          </div>
          {options.map(({ key, label, subtitle, icon, color, onSelect, disabled }) => (
            <DropdownMenuItem
              key={key}
              onSelect={onSelect}
              disabled={disabled}
              className="cursor-pointer rounded-md px-2 py-2"
            >
              <div
                className={`flex items-center justify-center h-7 w-7 rounded-md mr-2.5 ${color}`}
              >
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium leading-tight">{label}</div>
                <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                  {subtitle}
                </div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {walk && (
        <FeatureTour
          tourKey={`page-help-${walk[0].id}`}
          steps={walk}
          autoStart={false}
          isOpen
          onOpenChange={(next) => !next && setWalk(null)}
          onStepId={handleStepId}
          collapsible
        />
      )}
    </>
  );
}
